import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import _ from 'lodash';
import { BreadcrumbService, EnumValues } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Asset } from '@app/models/asset/asset';
import { InventoryTransaction, InventoryTransactionLine } from '@app/models/knitting/kntiting-requistion-report';
import { Roll } from '@app/models/knitting/roll';
import { ProductionCard, ProductionCardBalance, ProductionCardStatus } from '@app/models/production/production-card';
import { ProductionCardBundle, ProductionCardBundleStatus } from '@app/models/production/production-card/production-card-bundle';
import { BatchingRoles } from '@app/models/roles/roles-group';
import { AssetProxyService } from '@app/proxies/asset/asset.proxy.service';
import { ProductionCardBundleProxyService } from '@app/proxies/production/production-card-bundle.proxy.service';
import { BarcodeScannerService } from '@app/services/barcode-scanner.service';
import { EnumService } from '@app/services/enum.service';
import { IndividualProductionCardService } from '@app/services/individual-production-card.service';
import { ProductionCardBundleService } from '@app/services/production-card-bundle.service';
import { UserAuthenticationService } from '@app/services/user-authentication.service';
import { KnittingUtil } from '@app/utilities/knitting-util';
import { ProductionCardBundleStatusUtil } from '@app/utilities/production-card-bundle-status.util';
import { BatchingDetailsViewMode, ProductionCardStatusUtil } from '@app/utilities/production-card-status.util';

@Component({
  selector: 'app-production-card-bundles',
  templateUrl: './production-card-bundles.component.html',
  styleUrl: './production-card-bundles.component.scss',
})
export class ProductionCardBundlesComponent implements OnInit, OnDestroy {
  rollNumberToAdd = new FormControl<string>('');
  selectedBundleStatusControl = new FormControl<number | null>(null);
  selectedBundleAssetControl = new FormControl<string | null>(null);

  batchFoldingMachines: Asset[] = [];
  private readonly BATCH_FOLDING_MACHINE_TYPE = '69c60a06e1ea131afad2123b';

  private readonly route = inject(ActivatedRoute);
  private readonly enumService = inject(EnumService);
  private readonly individualProductionCardService = inject(IndividualProductionCardService);
  readonly productionCardBundleService = inject(ProductionCardBundleService);
  private readonly productionCardBundleProxy = inject(ProductionCardBundleProxyService);
  private readonly assetProxyService = inject(AssetProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly barcodeScannerService = inject(BarcodeScannerService);
  private readonly translate = inject(TranslateService);
  private readonly userAuthenticationService = inject(UserAuthenticationService);

  private barcodeScannerSubscription: Subscription | null = null;

  productionCardBundleStatusValues: EnumValues;
  ProductionCardBundleStatus = ProductionCardBundleStatus;

  form = new FormGroup({
    overrideTransferUnitView: new FormControl<boolean>(false),
  });

  activeInventoryTransaction = computed<InventoryTransaction | null>(() => {
    const transactions = this.individualProductionCardService.activeInventoryTransactionsInBatchingSignal();
    return transactions.length > 0 ? transactions[0] : null;
  });

  get productionCard(): ProductionCard | null {
    if (!this.individualProductionCardService.isInitialized()) return null;
    return this.individualProductionCardService.getProductionCardOrThrow();
  }
  get productionCardBundles() {
    if (!this.individualProductionCardService.isInitialized()) {
      return [];
    }
    return this.individualProductionCardService.productionCardBundles;
  }

  showTransferUnitRollsComponent = signal(false);

  fabricFormValues: EnumValues;

  addRollToBundles = signal(false);

  //#region Role helpers
  get isBatchingAdmin(): boolean {
    return this.userAuthenticationService.roles.includes(BatchingRoles.BatchingAdmin);
  }
  //#endregion

  //#region Production Card Status and View Mode Getters
  get showCreateBundlesButton(): boolean {
    if (!this.individualProductionCardService.isInitialized() || !this.isBatchingAdmin) {
      return false;
    }
    const numberOfPorts = this.individualProductionCardService.dyeingAsset?.dyeingMachineDetails?.numberOfPorts;
    return ProductionCardStatusUtil.ShowCreateBundlesButton(
      this.individualProductionCardService.productionCardBundles,
      this.individualProductionCardService.getProductionCardOrThrow().status,
      numberOfPorts,
    );
  }

  get viewMode(): BatchingDetailsViewMode {
    if (!this.individualProductionCardService.isInitialized()) {
      return null;
    }
    return ProductionCardStatusUtil.BatchingDetailsViewMode(
      this.individualProductionCardService.productionCardBundles,
      this.individualProductionCardService.getProductionCardOrThrow().status,
    );
  }

  get isReadOnlyMode(): boolean {
    if (!this.individualProductionCardService.isInitialized() || !this.isBatchingAdmin) {
      return true;
    }
    return !ProductionCardStatusUtil.CanEditBatching(this.individualProductionCardService.getProductionCardOrThrow().status);
  }
  //#endregion

  //#region Fabric getters

  get fabrics() {
    return this.individualProductionCardService.fabrics;
  }

  //#endregion

  get selectedBundle() {
    return this.productionCardBundleService.getSelectedBundle();
  }

  canBundleBeEdited(bundle: ProductionCardBundle): boolean {
    return this.isBatchingAdmin && !ProductionCardBundleStatusUtil.isReadOnly(bundle.status);
  }

  get productionCardPlannedBalances(): ProductionCardBalance[] {
    if (!this.individualProductionCardService.isInitialized()) {
      return [];
    }

    const entries: ProductionCardBalance[] = [];

    this.individualProductionCardService.getProductionCardOrThrow().productionCardEntries.forEach((pb) => {
      const currentBalance = this.getBalancePerFabricCode(pb.fabricCode);
      const rollCount = this.getRollCountPerFabricCode(pb.fabricCode);

      const fabric = this.individualProductionCardService.workOrder?.fabrics.find((f) => f.fabricCode === pb.fabricCode)?.fabric;

      entries.push({
        fabricCode: pb.fabricCode,
        longDescription: fabric?.longDescription || 'N/A',
        type: _.isNil(fabric?.fabricForm) ? 'N/A' : this.fabricFormValues[fabric!.fabricForm!],
        planned: pb.quantity,
        currentBalance: currentBalance,
        rollCount: rollCount,
      });
    });

    return entries;
  }

  rolls = computed<Roll[]>(() => {
    const transactions = this.individualProductionCardService.activeInventoryTransactionsInBatchingSignal();

    // Combine roll numbers from ALL transactions
    const transactionRollNumbers = transactions.flatMap((tx) => (tx.lines || []).map((line) => line.lotItemKey.number));
    const uniqueRollNumbers = new Set(transactionRollNumbers);

    if (
      this.productionCard?.status === ProductionCardStatus.Brd ||
      this.productionCard?.status === ProductionCardStatus.ReadyForFinishing
    ) {
      return this.individualProductionCardService.availableRolls;
    }
    const rolls = this.individualProductionCardService.availableRolls.filter((r) => uniqueRollNumbers.has(r.number!));

    return rolls.map((r) => {
      // Find override weight from any transaction that has this roll
      const lineWithRoll = transactions.flatMap((tx) => tx.lines || []).find((line) => line.lotItemKey.number === r.number);

      return {
        ...r,
        overrideWeight: lineWithRoll?.quantity,
      };
    });
  });

  private breadcrumbService = inject(BreadcrumbService);

  constructor() {
    effect(
      () => {
        const card = this.individualProductionCardService.productionCard();
        if (_.isNil(card)) return;

        const workOrder = this.individualProductionCardService.workOrder;
        this.breadcrumbService.set([
          { label: 'Planning' },
          {
            label: workOrder?.orderNumber ?? '',
            route: ['/production-cards-details'],
            queryParams: { id: workOrder?.id, workOrderId: workOrder?.id },
          },
          { label: card.productionCardNumber ?? '', route: `/production-card/${card.productionCardNumber}/details` },
          { label: this.translate.instant('Production_Card_Bundles_Bundles') },
        ]);
      },
      { allowSignalWrites: true },
    );
    this.fabricFormValues = this.enumService.getEnum('FabricForm', 'itemEnums')?.values || {};
    this.productionCardBundleStatusValues = this.enumService.getEnum('ProductionCardBundleStatus', 'productionEnums')?.values || {};

    this.loadBatchFoldingMachines();

    const existingBundle = this.productionCardBundleService.getSelectedBundle();
    if (existingBundle) {
      this.selectedBundleStatusControl.setValue(existingBundle.status, { emitEvent: false });
      this.selectedBundleAssetControl.setValue(existingBundle.assetId ?? null, { emitEvent: false });
    }

    this.individualProductionCardService.isInitialized$.subscribe(() => {
      this.getTransferUnitRolls();
      this.individualProductionCardService.proxyGetActiveInventoryTransactionsInBatching().subscribe(() => {
        this.showTransferUnitRollsComponent.set(false);
        this.getTransferUnitRolls();
      });
      this.individualProductionCardService.proxyGetRollsForReprocess().subscribe();
    });

    this.selectedBundleStatusControl.valueChanges.subscribe((newStatus) => {
      if (!_.isNil(newStatus)) {
        this.changeStatus(newStatus);
      }
    });

    this.barcodeScannerSubscription = this.barcodeScannerService.barcode$.subscribe((barcode) => {
      const activeTag = document.activeElement?.tagName.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea') {
        return;
      }

      if (barcode) {
        this.rollNumberToAdd.setValue(barcode);
        this.addRollToBundle();
      }
    });
  }

  //#region Lifecycle Hooks and Methods

  ngOnInit() {
    this.route.params.subscribe((params) => {
      const productionCardNumber = params['productionCardNumber'];
      if (productionCardNumber) {
        this.individualProductionCardService.initialize(productionCardNumber).subscribe({
          error: (error) => {
            console.error('Failed to initialize production card:', error);
          },
        });
      }
    });
  }

  ngOnDestroy() {
    this.barcodeScannerSubscription?.unsubscribe();
  }

  getTransferUnitRolls() {
    return this.individualProductionCardService.proxyGetAvailableRolls().subscribe({
      next: () => {
        this.showTransferUnitRollsComponent.set(true);
      },
      error: () => {
        this.showTransferUnitRollsComponent.set(true);
      },
    });
  }

  //#endregion

  //#region Bundle Actions and Helpers
  showActualWeight(bundle: ProductionCardBundle): boolean {
    return _.isNil(bundle?.actualWeightInLbs) ? false : bundle!.actualWeightInLbs! > 0;
  }

  selectBundle(bundleId: string) {
    /* this.selectedBundleStatusControl.setValue(null, { emitEvent: false });
    this.selectedBundleAssetControl.setValue(null, { emitEvent: false }); */
    this.productionCardBundleService.selectBundle(bundleId).subscribe((res) => {
      if (!res) {
        console.error('Failed to select bundle with ID:', bundleId);
        return;
      }
      this.selectedBundleStatusControl.setValue(this.selectedBundle!.status, { emitEvent: false });
      this.selectedBundleAssetControl.setValue(this.selectedBundle!.assetId ?? null, { emitEvent: false });
    });
  }

  loadBatchFoldingMachines() {
    this.assetProxyService.filterAssets({ assetTypeReference: this.BATCH_FOLDING_MACHINE_TYPE }).subscribe({
      next: (res) => {
        this.batchFoldingMachines = res.data ?? [];
      },
    });
  }

  updateBundleAsset(assetId: string | null) {
    if (_.isNil(this.selectedBundle)) return;
    this.productionCardBundleProxy.updateAssetId(this.selectedBundle.id, assetId).subscribe({
      next: (res) => {
        if (res.data) {
          this.selectedBundle!.assetId = res.data.assetId;
        }
      },
      error: () => {
        this.selectedBundleAssetControl.setValue(this.selectedBundle!.assetId ?? null, { emitEvent: false });
      },
    });
  }

  addRollToBundle() {
    const rollNumber = this.rollNumberToAdd.value;
    if (!rollNumber || rollNumber.trim() === '') {
      console.error('Roll number is required to add to bundle.');
      return;
    }

    const selectedBundle = this.selectedBundle;
    if (selectedBundle && selectedBundle.rollNumbers.includes(rollNumber)) {
      GlobalMessageUtil.warn(
        this.translate.instant('Roll_Already_In_Bundle'),
        this.translate.instant('Roll_Already_In_Bundle_Message', { rollNumber }),
      );
      return;
    }

    this.productionCardBundleService.addRollToBundle(rollNumber);
    this.rollNumberToAdd.setValue('');
  }

  createBundles() {
    this.productionCardBundleService.createBundles();
  }

  removeRollFromBundle(rollNumber: string) {
    if (!rollNumber) {
      console.error('Roll number is required to remove from bundle.');
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('Confirm_Remove_Roll_Message', { rollNumber }),
      header: this.translate.instant('Confirm_Remove_Roll'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('Yes'),
      rejectLabel: this.translate.instant('Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.productionCardBundleService.addRollToBundle(rollNumber);
      },
    });
  }

  //#endregion

  //#region Status Helpers

  showStatusSelect(): boolean {
    if (_.isNil(this.selectedBundle) || !this.isBatchingAdmin) return false;
    return ProductionCardBundleStatusUtil.getValidNextStatuses(this.selectedBundle.status, this.selectedBundle).length > 0;
  }

  getStatusSelectOptions(): { label: string; value: number; disabled: boolean }[] {
    if (_.isNil(this.selectedBundle)) return [];
    const validStatuses = ProductionCardBundleStatusUtil.getValidNextStatuses(this.selectedBundle.status, this.selectedBundle);
    const statusOptions = [];
    statusOptions.push({
      label: this.productionCardBundleStatusValues[this.selectedBundle.status],
      value: this.selectedBundle.status,
      disabled: true,
    });

    statusOptions.push(
      ...validStatuses.map((status) => ({
        label: this.productionCardBundleStatusValues[status],
        value: status,
        disabled: false,
      })),
    );

    return statusOptions;
  }

  canReturnToProcessing(): boolean {
    return ProductionCardBundleStatusUtil.canReturnToProcessing(this.selectedBundle);
  }

  //#endregion

  //#region Balance and calculations helpers
  private getBalancePerFabricCode(fabricCode: string): number {
    const transactions = this.individualProductionCardService.activeInventoryTransactionsInBatchingSignal();
    const transactionRollNumbers = transactions.flatMap((tx) => (tx.lines || []).map((line) => line.lotItemKey.number));
    const uniqueRollNumbers = new Set(transactionRollNumbers);
    const rolls = this.individualProductionCardService.availableRolls.filter(
      (r) => r.expectedFabricCode === fabricCode && uniqueRollNumbers.has(r.number!),
    );

    //now to add them up, we need to get the line.quantity from the transaction for each roll
    let total = 0;
    rolls.forEach((roll) => {
      const allLines = transactions.flatMap((tx) => tx.lines || []);
      total += allLines.find((line: InventoryTransactionLine) => line.lotItemKey.number === roll.number!)?.quantity || 0;
    });
    return total;
  }

  private getRollCountPerFabricCode(fabricCode: string): number {
    const transactions = this.individualProductionCardService.activeInventoryTransactionsInBatchingSignal();
    const transactionRollNumbers = transactions.flatMap((tx) => (tx.lines || []).map((line) => line.lotItemKey.number));
    const uniqueRollNumbers = new Set(transactionRollNumbers);
    const rolls = this.individualProductionCardService.availableRolls.filter(
      (r) => r.expectedFabricCode === fabricCode && uniqueRollNumbers.has(r.number!),
    );
    return rolls.length;
  }

  //#endregion

  //#region Status Change

  changeStatus(newStatus: number): void {
    if (_.isNil(this.selectedBundle)) return;
    if (newStatus === this.selectedBundle.status) return;

    if (newStatus === ProductionCardBundleStatus.ReadyForWeighing && _.isNil(this.selectedBundleAssetControl.value)) {
      this.selectedBundleStatusControl.setValue(this.selectedBundle.status, { emitEvent: false });
      GlobalMessageUtil.warn(this.translate.instant('Warning'), this.translate.instant('Production_Card_Bundles_AssetRequired'));
      return;
    }

    if (newStatus === ProductionCardBundleStatus.ReadyForWeighing) {
      this.confirmationService.confirm({
        message: this.translate.instant('Confirm_Send_To_Weighing_Message'),
        header: this.translate.instant('Confirm_Bundle_Weighing'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('Yes_Send_To_Weighing'),
        rejectLabel: this.translate.instant('Cancel'),
        acceptButtonStyleClass: 'p-button-success',
        rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
        accept: () => {
          this.productionCardBundleService.changeStatus(newStatus);
        },
        reject: () => {
          this.selectedBundleStatusControl.setValue(this.selectedBundle!.status, { emitEvent: false });
        },
      });
    } else if (newStatus === ProductionCardBundleStatus.Processing) {
      this.confirmationService.confirm({
        message: this.translate.instant('Confirm_Return_To_Processing_Message', { bundleNumber: this.selectedBundle.number }),
        header: this.translate.instant('Confirm_Return_To_Processing'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('Yes_Return_To_Processing'),
        rejectLabel: this.translate.instant('Cancel'),
        acceptButtonStyleClass: 'p-button-warning',
        rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
        accept: () => {
          this.productionCardBundleService.changeStatus(newStatus);
        },
        reject: () => {
          this.selectedBundleStatusControl.setValue(this.selectedBundle!.status, { emitEvent: false });
        },
      });
    } else {
      this.productionCardBundleService.changeStatus(newStatus);
    }
  }

  //#endregion

  getBundleActualWeightInLbs(productionCardBundle: ProductionCardBundle): number {
    const rolls = productionCardBundle.rolls || [];
    return rolls.reduce((total, roll) => total + KnittingUtil.getCurrentWeightInLbs(this.getRollStrict(roll)), 0);
  }

  //#region Strict Getters

  getBundleStrict(item: unknown): ProductionCardBundle {
    return item as ProductionCardBundle;
  }

  getRollStrict(item: unknown) {
    return item as Roll;
  }

  getProductionCardBalanceStrict(item: unknown): ProductionCardBalance {
    return item as ProductionCardBalance;
  }

  //#endregion

  //#region Fabric helpers
  getFabric(fabricCode: string) {
    return this.individualProductionCardService.workOrder?.fabrics.find((f) => f.fabricCode === fabricCode)?.fabric;
  }
  //#endregion

  getStatusSeverity(status: number): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    switch (status) {
      case ProductionCardBundleStatus.Processing:
        return 'info';
      case ProductionCardBundleStatus.ReadyForWeighing:
        return 'warn';
      case ProductionCardBundleStatus.Weighed:
        return 'success';
      case ProductionCardBundleStatus.BRD:
        return 'contrast';
      default:
        return 'secondary';
    }
  }
}
